import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { last } from 'ramda'
import { useCMSPageField } from '@/cms/fields'
import { reviewsAPIClient } from '@/scopes/products/user-reviews/infrastructure/api/reviews'
import { useUserReviewsStore } from './infrastructure/store'

export function useUserReviews() {
  const accommodationId = useCMSPageField('PDP.id')
  const {
    showOnlyInWebsiteLanguage,
    setShowOnlyInWebsiteLanguage,
    partyCompositionGroupId,
    setPartyCompositionGroupId,
    sorting,
    setSorting,
  } = useUserReviewsStore()

  const { isLoading, isFetching, data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetched } = useInfiniteQuery({
    queryKey: ['user-reviews', sorting, showOnlyInWebsiteLanguage, partyCompositionGroupId],
    queryFn: ({ pageParam }) =>
      reviewsAPIClient.getUserReviews({
        contextItemId: accommodationId,
        sorting,
        showOnlyInWebsiteLanguage,
        partyCompositionGroupId,
        offset: pageParam ? pageParam - 10 : 0,
        limit: pageParam ? 20 : 10,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (pages.length === 1 && lastPage.amount <= 10) {
        return undefined
      }
      if (pages.length > 1 && lastPage.reviews.length < 20) {
        return undefined
      }
      return pages.length * 20
    },
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  })

  const reviewPages = data?.pages || []

  return {
    isLoading,
    isFetching,
    reviewPages,
    partyCompositionGroups: last(data?.pages ?? [])?.partyCompositionGroups ?? [],
    showOnlyInWebsiteLanguage,
    setShowOnlyInWebsiteLanguage,
    partyCompositionGroupId,
    setPartyCompositionGroupId,
    sorting,
    setSorting,
    hasNextPage,
    isFetchingNextPage,
    reviewsAmount: reviewPages.length > 0 ? reviewPages[0].amount : 0,
    showNextReviews: fetchNextPage,
    isFetched,
  }
}

export function useUserReviewsInitialData() {
  const accommodationId = useCMSPageField('PDP.id')
  const { isLoading, data, isFetched } = useQuery({
    queryKey: ['initial-user-reviews'],
    queryFn: () =>
      reviewsAPIClient.getUserReviews({
        contextItemId: accommodationId,
        sorting: '-date',
        showOnlyInWebsiteLanguage: false,
        partyCompositionGroupId: '',
        offset: 0,
        limit: 2,
      }),
  })

  return {
    isLoading,
    reviews: data?.reviews ?? [],
    isFetched,
    reviewsAmount: data?.amount ?? 0,
    partyCompositionGroups: data?.partyCompositionGroups ?? [],
  }
}
